import {
  WalletKeys,
  WalletsTableSortKeys,
} from 'api/types/httpsTypes/contracts';
import {
  FilterState,
  useWalletsSearchFilters,
  WalletPeriods,
} from './usePageParams';
import { ChainsIcon } from 'modules/shared-components/exchange/exchange-icon';
import { chainAsset, chainName, Chains } from 'api/types/httpsTypes/d-wallets';
import { useEffect, useState } from 'react';
import { useDebouncedState } from 'utils/useDebounce';

import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  BalanceRangeFilter,
  LabelsFilter,
  PnlFilter,
  ReturnsFilter,
  VolumeRangeFilter,
} from './ResearchFilters';
import { isNotNil } from 'modules/utils/isNotNil';
import { allSuffixes, formatNumberWithSuffix } from 'utils/FormatNumber';
import { twMerge } from 'tailwind-merge';
import { isValidAddress } from 'helpers/api/web3Utils';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../../components/dropdown/sort-dropdown';
import {
  ActivteFiltersBar,
  FilterTag,
  SelectFiltersBar,
} from '../../../components/filters';
import QuickFilters from '../../../components/filters/quickFilters';
import TextInput from '../../../components/inputs/text-input/text-input';
import { useGlobalChain } from 'modules/routing/chainProvider';

export const SORT_OPTIONS = [
  {
    label: 'Balance',
    value: `-${WalletsTableSortKeys.Balance}`,
  },
  {
    label: 'Volume',
    value: `-${WalletsTableSortKeys.Volume}`,
  },
  {
    label: 'Tokens',
    value: `-${WalletsTableSortKeys.UniqueTokens}`,
  },
  {
    label: 'Number of Wins',
    value: `-${WalletsTableSortKeys.NOfWins}`,
  },
  {
    label: 'Total Returns',
    value: `-${WalletsTableSortKeys.Returns}`,
  },

  {
    label: 'Total PnL',
    value: `-${WalletsTableSortKeys.PnL}`,
  },

  {
    label: 'Holding Time',
    value: `-${WalletsTableSortKeys.HoldingTime}`,
  },
  {
    label: 'Last Swap',
    value: `-${WalletsTableSortKeys.LastSwap}`,
  },
];

export const CHAINS_OPTIONS = [
  {
    value: Chains.Ethereum,
    label: chainName(Chains.Ethereum),
    icon: <ChainsIcon imgName={Chains.Ethereum} />,
  },
  {
    value: Chains.Base,
    label: chainName(Chains.Base),
    icon: <ChainsIcon imgName={Chains.Base} />,
  },
  {
    value: Chains.BinanceChain,
    label: chainName(Chains.BinanceChain),
    icon: <ChainsIcon imgName={Chains.BinanceChain} />,
  },
];

export const WalletsFiltersBar = () => {
  const { filtersState: filters, setFilters } = useWalletsSearchFilters();
  const [walletAddress, debouncedWalletAddress, setWalletAddress] =
    useDebouncedState('', 250);
  const [selectedChain] = useGlobalChain();
  const chain = selectedChain;
  const [addressError, setAddressError] = useState(false);

  useEffect(() => {
    if (debouncedWalletAddress) {
      setAddressError(!isValidAddress(debouncedWalletAddress, selectedChain));
      if (isValidAddress(debouncedWalletAddress, selectedChain)) {
        updateFilters({
          addressFilter: debouncedWalletAddress,
        });
      }
    }
  }, [debouncedWalletAddress]);

  const handleWalletAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setWalletAddress(inputValue);

    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        addressFilter: undefined,
        tradingStyle: WalletKeys.TopCopy,
      });
    } else {
      updateFilters({
        tradingStyle: undefined,
      });
    }
  };

  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filters, ...update });
  };

  const quickFilterTopSniper = () => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys.PnL}`,
      tradingStyle: WalletKeys.TopSniper,
    } as FilterState);
  };

  const quickFilterTopCopy = () => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys.PnL}`,
      tradingStyle: WalletKeys.TopCopy,
    } as FilterState);
  };

  const quickFilterTopTrader = () => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys.PnL}`,
      tradingStyle: WalletKeys.Trader,
    } as FilterState);
  };

  const quickFilterEarlyApe = () => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys.PnL}`,
      tradingStyle: WalletKeys.EarlyApe,
    } as FilterState);
  };

  const quickFilterOptions = [
    {
      label: 'Best To Copy',
      onClick: quickFilterTopCopy,
      condition: filters.tradingStyle === WalletKeys.TopCopy,
      extraClasses: 'flex',
    },
    {
      label: 'Top Snipers',
      onClick: quickFilterTopSniper,
      condition: filters.tradingStyle === WalletKeys.TopSniper,
      extraClasses: 'hidden lg:flex',
    },
    {
      label: 'Top Early Apes',
      onClick: quickFilterEarlyApe,
      condition: filters.tradingStyle === WalletKeys.EarlyApe,
      extraClasses: 'flex',
    },
    {
      label: 'Top Traders',
      onClick: quickFilterTopTrader,
      condition: filters.tradingStyle === WalletKeys.Trader,
      extraClasses: 'flex',
    },
  ];
  const nOfFiltersActive = [
    filters.tradingStyle,
    filters.addressFilter,
    filters.minVolume,
    filters.maxVolume,
    filters.minReturns,
    filters.maxReturns,
    filters.minPnl,
    filters.maxPnl,
    filters.minBalance,
    filters.maxBalance,
  ].filter((value) => value !== undefined && value && value !== '').length;
  return (
    <div className="xxs:mx-1 space-y-1 lg:mx-0">
      <SelectFiltersBar
        className="xxs:justify-start space-x-2"
        icon={
          <div
            className={twMerge(
              'xxs:p-1 flex xxs:space-x-1 items-center',
              nOfFiltersActive
                ? 'xxs:hover:text-dex-white'
                : ' xxs:hover:text-dex-white xxs:text-dex-white-secondary xxs:px-2 '
            )}
          >
            <FunnelIcon aria-hidden="true" width={16} height={16} />
            {nOfFiltersActive ? <div>{nOfFiltersActive}</div> : ''}
          </div>
        }
        isCondensed={true}
        sort={
          <div className="xxs:flex xxs:space-x-2">
            <div className="">
              <TextInput
                name="addressFilter"
                error={addressError ? 'Address not valid' : ''}
                value={walletAddress}
                prefix={
                  <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
                }
                onChange={handleWalletAddressInputChange}
                placeholder="Wallet Address"
              />
            </div>

            <div className="lg:hidden">
              <Sort
                value={filters.sort}
                onChange={(value) => {
                  updateFilters({
                    sort: value,
                  });
                }}
              />
            </div>
          </div>
        }
      >
        <div className="xxs:space-y-2">
          <div className="xxs:hidden lg:block xxs:text-dex-white-secondary">
            Use advanced filters to find the best wallets to copy-trade.
          </div>
          <TextInput
            name="addressFilter"
            error={addressError ? 'Address not valid' : ''}
            value={walletAddress}
            prefix={
              <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
            }
            onChange={handleWalletAddressInputChange}
            placeholder="Wallet Address"
          />

          <LabelsFilter />
          <BalanceRangeFilter />
          <VolumeRangeFilter />
          <PnlFilter />
          <ReturnsFilter />
        </div>
      </SelectFiltersBar>
      <QuickFilters filterOptions={quickFilterOptions} />
      <ActivteFiltersBar>
        {filters.addressFilter && (
          <FilterTag
            onRemove={() => {
              setWalletAddress('');
              return updateFilters({
                addressFilter: undefined,
                tradingStyle: WalletKeys.TopCopy,
              });
            }}
            label={`Wallet: ${filters.addressFilter}` || ''}
          />
        )}
        {filters.tradingStyle && (
          <FilterTag
            onRemove={() => {
              if (filters.addressFilter) {
                updateFilters({ ...filters, tradingStyle: undefined });
              }
            }}
            label={`${
              filters.tradingStyle === WalletKeys.TopSniper
                ? '🎯 Top Sniper'
                : filters.tradingStyle === WalletKeys.EarlyApe
                ? '🐒 Early Ape'
                : filters.tradingStyle === WalletKeys.Trader
                ? '📈 Top Trader'
                : '📄 Best To Copy'
            }`}
          />
        )}
        {isNotNil(filters.minVolume) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minVolume: undefined,
              })
            }
            label={`Volume > $${formatNumberWithSuffix(filters.minVolume, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.maxVolume) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxVolume: undefined,
              })
            }
            label={`Volume < $${formatNumberWithSuffix(filters.maxVolume, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.minPnl) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minPnl: undefined,
              })
            }
            label={`PnL > $${formatNumberWithSuffix(filters.minPnl, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.maxPnl) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxPnl: undefined,
              })
            }
            label={`PnL < $${formatNumberWithSuffix(filters.maxPnl, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}

        {isNotNil(filters.minReturns) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minReturns: undefined,
              })
            }
            label={`Returns > ${formatNumberWithSuffix(
              filters.minReturns * 100,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}%`}
          />
        )}
        {isNotNil(filters.maxReturns) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxReturns: undefined,
              })
            }
            label={`Returns < ${formatNumberWithSuffix(
              filters.maxReturns * 100,
              {
                suffixes: allSuffixes,
                precision: 0,
              }
            )}%`}
          />
        )}

        {isNotNil(filters.minBalance) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minBalance: undefined,
              })
            }
            label={`Balance > ${formatNumberWithSuffix(filters.minBalance, {
              suffixes: allSuffixes,
              precision: 0,
            })}${chainAsset(chain)}`}
          />
        )}
        {isNotNil(filters.maxBalance) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxBalance: undefined,
              })
            }
            label={`Balance < ${formatNumberWithSuffix(filters.maxBalance, {
              suffixes: allSuffixes,
              precision: 0,
            })}${chainAsset(chain)}`}
          />
        )}
      </ActivteFiltersBar>
    </div>
  );
};

export function Sort(props: Omit<SortDropdownProps<string>, 'options'>) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:py-1.5 xxs:px-2 h-auto"
      {...props}
      options={SORT_OPTIONS}
    />
  );
}
